import type { UseSoftphoneOnCallHeaderReturn } from './index.types';
import { useCallDuration } from 'Modules/softphone/components/SoftphoneHeldCalls/hooks/useCallDuration';
import { useSoftphoneContact } from 'Modules/softphone/hooks';
import { useConferenceCallManager } from 'Modules/softphone/hooks/useConferenceCallManager';
import { useSoftphoneStore } from 'Modules/softphone/store';
import { formatDuration } from 'Utils/formatDuration';
import { formatNumberWithNationalCode } from 'Utils/phoneUtil';

/**
 * useSoftphoneOnCallHeader - Hook to provide data for the on-call header
 *
 * Centralizes the logic for displaying caller information and call duration
 * in the softphone header during an active call.
 *
 * During an active conference, shows "Conference call" and uses the conference
 * start time for the duration instead of the individual participant's.
 *
 * @returns Formatted caller name, number, and call duration
 */
export const useSoftphoneOnCallHeader = (): UseSoftphoneOnCallHeaderReturn => {
  const { activeCalls, currentCallId, activeConference } = useSoftphoneStore(
    (state) => ({
      activeCalls: state.activeCalls,
      currentCallId: state.currentCallId,
      activeConference: state.activeConference,
    })
  );

  const currentCall = currentCallId ? activeCalls.get(currentCallId) : null;

  const { isConferenceActive } = useConferenceCallManager();
  const isCurrentCallInConference =
    isConferenceActive && Boolean(currentCall?.conferenceId);

  // Get caller information for active call
  const { displayName: name, phoneNumber } = useSoftphoneContact();

  const formattedPhone = formatNumberWithNationalCode(phoneNumber);

  // During a conference, show "Conference call" instead of the participant name
  const callerName = isCurrentCallInConference
    ? 'Conference call'
    : name || formattedPhone || '';
  const callerNumber = isCurrentCallInConference ? '' : formattedPhone || '';

  // Use conference startTime when in a conference, otherwise use the call's
  // answerTime so connected-call timers do not include outbound ringing time.
  const effectiveStartTime = isCurrentCallInConference
    ? activeConference?.startTime
    : currentCall?.answerTime;

  // Use hook to get live-updating call duration
  const durationInSeconds = useCallDuration(
    effectiveStartTime,
    currentCall?.duration || 0
  );

  // Format duration for display
  const callDuration = formatDuration(effectiveStartTime ? durationInSeconds : 0);

  return {
    callerName,
    callerNumber,
    callDuration,
  };
};
